import {ChangeDetectionStrategy, Component, computed, Inject, Injectable, Signal, signal} from "@angular/core";
import {AkseptertSignaturmetode, DokumentVersjonModell, GruppeId, GruppeMedlemListeModell, HtmlTekst, SendTilSigneringSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {toSignal} from "@angular/core/rxjs-interop";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatCheckbox} from "@angular/material/checkbox";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {MatTooltip} from "@angular/material/tooltip";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {SigneringVelgAksepterteMetoderKomponent} from "../../../signering/komponenter/signering-velg-aksepterte-metoder/signering-velg-aksepterte-metoder";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {TranslateModule} from "@ngx-translate/core";
import {VelgMedlemmerKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer/velg-medlemmer";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {erLikeListerIgnorerRekkefølge} from "felles-ng2/støttefunksjoner/lister";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {Observable} from "rxjs";
import {mapTilData, terminerEtterFørsteRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {somEntitetId} from "felles-ng2/modell/modellHjelpere";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MeldingSendtIkonKomponent} from "web-ng2/delt/komponenter/melding-sendt-ikon/melding-sendt-ikon";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {fadeInnUtAnimasjon} from "web-ng2/delt/animasjoner";
import {DokumentSigneringDatatjeneste, SendTilSigneringValideringsresultatDLM, valideringsfeilSomTekst} from "felles-ng2/tjenester/datalager/DokumentSigneringDatatjeneste";
import {map, tap} from "rxjs/operators";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";

const debug = initDebugger('DokumentSendTilSigneringDialogKomponent');

/**
 * Dialog med skjema for å sende Dokument til signering.
 */
@Component({
    selector: 'dokument-send-til-signering-dialog',
    templateUrl: 'dokument-send-til-signering-dialog.html',
    styleUrl: 'dokument-send-til-signering-dialog.scss',
    host: {'data-testid': 'dokument-send-til-signering-dialog'},
    animations: [fadeInnUtAnimasjon('0.5s')],
    imports: [
        MatButton,
        MatCheckbox,
        MatDialogActions,
        MatDialogContent,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatToolbar,
        MatTooltip,
        ReactiveFormsModule,
        SigneringVelgAksepterteMetoderKomponent,
        TipsKomponent,
        TranslateModule,
        VelgMedlemmerKomponent,
        SubmitButtonKomponent,
        FormsModule,
        MeldingSendtIkonKomponent,
        InlineMeldingKomponent,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DokumentSendTilSigneringDialogKomponent {

    static vis(dialog: MatDialog, dokumentversjon: DokumentVersjonModell) {
        return dialog.open<DokumentSendTilSigneringDialogKomponent, DialogParametre, DokumentSendTilSigneringDialogRespons>(DokumentSendTilSigneringDialogKomponent,
          {
              data: {dokumentversjon, modus: 'sendTilSignering'} as DialogParametre,
              disableClose: true,
              ariaLabelledBy: 'dokument-send-til-signering-dialog-tittel',
              minHeight: 550,
              maxWidth: 700
          });
    }

    dokumentversjon: DokumentVersjonModell; //settes via DialogParametre

    skjema = new FormGroup({
        signatarer: new FormControl<GruppeMedlemListeModell[]>([], {nonNullable: true, validators: Validators.required}),
        beskjed: new FormControl<HtmlTekst>('', {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
        signaturmetoder: new FormControl<AkseptertSignaturmetode[]>([], {nonNullable: true, validators: Validators.required}),
        huskSignaturmetodeValg: new FormControl(false, {nonNullable: true})
    });

    modus = signal<'send' | 'kvittering'>('send');
    signaturmetoderEndret = computed(() => !erLikeListerIgnorerRekkefølge(this.signaturmetoderSattPåGruppe(), this.signaturmetoderValgt()));
    valgbareMedlemmer: Signal<GruppeMedlemListeModell[]>;
    valideringsfeil = signal<string[]>([]);
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    private signaturmetoderSattPåGruppe: Signal<AkseptertSignaturmetode[]>;
    private signaturmetoderValgt = toSignal(this.skjema.controls.signaturmetoder.valueChanges, {initialValue: []});

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<DokumentSendTilSigneringDialogKomponent, DokumentSendTilSigneringDialogRespons>,
                private avhengigheter: DokumentSendTilSigneringDialogKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                private navigasjon: Navigasjon) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.dokumentversjon = parametre.dokumentversjon;
        this.valgbareMedlemmer = toSignal(this.avhengigheter.gruppeMedlemmer(this.dokumentversjon.gruppeId), {initialValue: []});
        this.signaturmetoderSattPåGruppe = toSignal(this.avhengigheter.standardSignaturmetoder(this.dokumentversjon.gruppeId)
          .pipe(tap(metoder => this.skjema.controls.signaturmetoder.setValue(metoder))), {initialValue: []});
    }

    async sendTilSignering() {
        const valideringsresultat = await this.lagrer.utfør(() =>
          this.avhengigheter.sendTilSignering(this.dokumentversjon, this.somSendSpesifikasjon()));
        if (valideringsresultat.harFeil) {
            this.valideringsfeil.set(valideringsfeilSomTekst(valideringsresultat, this.valgbareMedlemmer())
              .map(feil => feil.oversett(this.oversettelser)));
            return;
        }
        this.modus.set('kvittering');
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    gaaTilHjemskjermen() {
        this.ferdig();
        this.navigasjon.gåTilForsiden();
    }

    //private hjelpemetoder

    private somSendSpesifikasjon(): SendTilSigneringSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            signatarer: verdier.signatarer!.map(somEntitetId),
            beskjed: verdier.beskjed!,
            signaturmetoder: verdier.signaturmetoder!,
            huskSignaturmetodeValg: verdier.huskSignaturmetodeValg!,
        };
    }

}

export type DokumentSendTilSigneringDialogRespons = boolean;

export type DialogParametre = { dokumentversjon: DokumentVersjonModell; };

@Injectable()
export class DokumentSendTilSigneringDialogKomponentAvhengigheter {

    constructor(private dokumentSigneringDatatjeneste: DokumentSigneringDatatjeneste, private gruppeDatatjeneste: GruppeDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    gruppeMedlemmer(id: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilgjengeligeMedMeta(id)
          .pipe(terminerEtterFørsteRequestRespons(), mapTilData());
    }

    standardSignaturmetoder(id: GruppeId): Observable<AkseptertSignaturmetode[]> {
        return this.gruppeDatatjeneste.hentMedMeta(id)
          .pipe(map(gruppe => gruppe.data.oppsett.aksepterteSignaturmetoder));
    }

    sendTilSignering(dokumentversjon: DokumentVersjonModell, spesifikasjon: SendTilSigneringSpesifikasjon): Promise<SendTilSigneringValideringsresultatDLM> {
        return this.dokumentSigneringDatatjeneste.sendTilSignering(dokumentversjon, spesifikasjon);
    }
}
